import "vanilla-cookieconsent/dist/cookieconsent.css";
import * as CookieConsent from "vanilla-cookieconsent";

// Google Analytics functions
const initializeGoogleAnalytics = () => {
  // Create and append the Google Analytics script
  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://www.googletagmanager.com/gtag/js?id=G-987LBTX4G6';
  document.head.appendChild(script);

  // Initialize gtag
  window.dataLayer = window.dataLayer || [];
  function gtag(){window.dataLayer.push(arguments);}
  window.gtag = gtag;
  gtag('js', new Date());
  gtag('config', 'G-987LBTX4G6');
  
  console.log('Google Analytics initialized');
};

const disableGoogleAnalytics = () => {
  // Disable Google Analytics
  if (window.gtag) {
    window.gtag('consent', 'update', {
      'analytics_storage': 'denied'
    });
  }
  
  // Remove GA cookies
  const gaCookies = document.cookie.split(';').filter(cookie => 
    cookie.trim().startsWith('_ga') || cookie.trim().startsWith('_gid')
  );
  
  gaCookies.forEach(cookie => {
    const cookieName = cookie.split('=')[0].trim();
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${window.location.hostname}`;
    document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  });
  
  console.log('Google Analytics disabled and cookies cleared');
};

// Custom cursor compatibility functions
const addCustomCursorStyles = () => {
  // Add cursor: none !important to all cookie consent elements
  const cookieElements = document.querySelectorAll('#cc-main *, [data-cc]');
  cookieElements.forEach(element => {
    element.style.cursor = 'none';
    element.style.setProperty('cursor', 'none', 'important');
  });
};

const refreshCustomCursor = () => {
  // Try to refresh the custom cursor's hoverable elements
  // This will work if the CustomCursor component is mounted
  const customCursorComponent = document.querySelector('.cursor-container');
  if (customCursorComponent && customCursorComponent.__vue__) {
    // If we can access the Vue component instance, refresh hoverables
    try {
      customCursorComponent.__vue__.refreshHoverElements();
    } catch (error) {
      console.log('Could not refresh custom cursor hoverables:', error);
    }
  }
  
  // Alternative: Dispatch a custom event that the cursor component can listen to
  window.dispatchEvent(new CustomEvent('cookieConsentRendered'));
};

const moveCookiePolicyToTextArea = () => {
  // Move the cookie policy footer into the text area
  const footer = document.querySelector('#cc-main .cm__footer');
  const textArea = document.querySelector('#cc-main .cm__texts');
  
  if (footer && textArea) {
    // Move the footer element to be the last child of the text area
    textArea.appendChild(footer);
    console.log('Cookie Policy link moved to text area');
  }
};

const formatDescriptionText = () => {
  // Ensure the description text has proper line breaks for better wrapping
  const descElement = document.querySelector('#cc-main .cm__desc');
  
  if (descElement) {
    const currentText = descElement.textContent || descElement.innerText;
    
    // Replace the text with proper line break before "Choose your cookie preferences."
    if (currentText.includes('Choose your cookie preferences.')) {
      const newText = currentText.replace(
        'browsing experience. Choose your cookie preferences.',
        'browsing experience.\nChoose your cookie preferences.'
      );
      descElement.textContent = newText;
      console.log('Description text formatted with line break');
    }
  }
};

const setupCustomCursorCompatibility = () => {
  // Add styles to ensure cursor compatibility
  addCustomCursorStyles();
  
  // Move cookie policy link to text area
  moveCookiePolicyToTextArea();
  
  // Format description text with proper line breaks
  formatDescriptionText();
  
  // DEBUG: Log what buttons are actually created (disabled for production)
  // const buttons = document.querySelectorAll('#cc-main .cm__btn');
  // console.log('Cookie consent buttons found:', buttons.length);
  // buttons.forEach((btn, index) => {
  //   console.log(`Button ${index}:`, {
  //     text: btn.textContent,
  //     classes: btn.className,
  //     isPrimary: !btn.classList.contains('cm__btn--secondary'),
  //     isSecondary: btn.classList.contains('cm__btn--secondary')
  //   });
  // });
  
  // Refresh custom cursor after a short delay to ensure DOM is updated
  setTimeout(() => {
    addCustomCursorStyles(); // Apply again in case elements were added
    refreshCustomCursor();
    moveCookiePolicyToTextArea(); // Try again in case DOM wasn't ready
    formatDescriptionText(); // Format text after DOM is ready
  }, 100);
  
  // Also refresh after a longer delay for safety
  setTimeout(() => {
    addCustomCursorStyles();
    refreshCustomCursor();
    moveCookiePolicyToTextArea(); // Try again for safety
    formatDescriptionText(); // Format text again for safety
  }, 500);
};

export default {
  install: (app) => {
    CookieConsent.run({
      // UI Options for bottom-right positioning and styling
      guiOptions: {
        consentModal: {
          layout: 'box',
          position: 'bottom right',
          equalWeightButtons: false,
          flipButtons: false
        },
        preferencesModal: {
          layout: 'box',
          position: 'right',
          equalWeightButtons: true,
          flipButtons: false
        }
      },

      // Cookie categories
      categories: {
        necessary: {
          enabled: true,
          readOnly: true
        },
        analytics: {
          enabled: false,
          autoClear: {
            cookies: [
              { name: /^_ga/ },
              { name: '_gid' },
              { name: /^_gat/ }
            ]
          }
        }
      },

      // Event handlers
      onFirstConsent: ({cookie}) => {
        console.log('First consent given:', cookie);
        if (CookieConsent.acceptedCategory('analytics')) {
          initializeGoogleAnalytics();
        }
        // Ensure custom cursor compatibility
        setupCustomCursorCompatibility();
      },

      onConsent: ({cookie}) => {
        console.log('Consent updated:', cookie);
        if (CookieConsent.acceptedCategory('analytics')) {
          initializeGoogleAnalytics();
        }
        // Ensure custom cursor compatibility
        setupCustomCursorCompatibility();
      },

      onChange: ({changedCategories, changedServices}) => {
        console.log('Consent changed:', changedCategories, changedServices);
        
        if (changedCategories.includes('analytics')) {
          if (CookieConsent.acceptedCategory('analytics')) {
            initializeGoogleAnalytics();
          } else {
            disableGoogleAnalytics();
          }
        }
        // Ensure custom cursor compatibility after changes
        setupCustomCursorCompatibility();
      },

      // Language and content configuration
      language: {
        default: 'en',
        translations: {
          en: {
            consentModal: {
              title: 'We value your privacy',
              description: 'We use cookies to enhance your browsing experience.\nChoose your cookie preferences.',
              acceptAllBtn: 'Accept All',
              acceptNecessaryBtn: 'Necessary Only',  
              showPreferencesBtn: 'Customize',
              footer: '<a href="/cookie-policy">Cookie Policy</a>'
            },
            preferencesModal: {
              title: 'Cookie Preferences',
              acceptAllBtn: 'Accept All',
              acceptNecessaryBtn: 'Necessary Only',
              savePreferencesBtn: 'Save Preferences',
              closeIconLabel: 'Close',
              serviceCounterLabel: 'Service|Services',
              sections: [
                {
                  title: 'Cookie Usage',
                  description: 'We use cookies to improve your experience. You can choose which categories to allow below.'
                },
                {
                  title: 'Strictly Necessary',
                  description: 'These cookies are essential for the website to function properly and cannot be disabled.',
                  linkedCategory: 'necessary'
                },
                {
                  title: 'Analytics',
                  description: 'These cookies help us understand how visitors interact with our website by collecting information anonymously.',
                  linkedCategory: 'analytics',
                  cookieTable: {
                    caption: 'Analytics Cookies',
                    headers: {
                      name: 'Cookie',
                      domain: 'Domain',
                      desc: 'Description'
                    },
                    body: [
                      {
                        name: '_ga',
                        domain: window.location.hostname,
                        desc: 'Used to distinguish users (expires in 2 years)'
                      },
                      {
                        name: '_gid',
                        domain: window.location.hostname,
                        desc: 'Used to distinguish users (expires in 24 hours)'
                      },
                      {
                        name: '_gat',
                        domain: window.location.hostname,
                        desc: 'Used to throttle request rate (expires in 1 minute)'
                      }
                    ]
                  }
                },
                {
                  title: 'More Information',
                  description: 'For questions about our cookie policy, please visit our <a href="/cookie-policy">Cookie Policy</a> page or <a href="/contact">contact us</a>.'
                }
              ]
            }
          }
        }
      }
    });

    // Initial setup for custom cursor compatibility
    // Run after the cookie consent is initialized
    setTimeout(() => {
      setupCustomCursorCompatibility();
    }, 100);

    // Make CookieConsent available globally for the footer link
    app.config.globalProperties.$cookieConsent = CookieConsent;
  }
}; 